import { extendZodWithOpenApi } from "zod-openapi";
extendZodWithOpenApi(z);

import { TableIdSchema } from "@/server/schemas/table-tables";
import { BlockBaseSchema, BlockIdSchema } from "block-system/types/base";
import { ZodSchema, z } from "zod";
import { blockType, fieldBlockTypeToTableFieldTypes } from "./constants";
import { FieldTypes } from "./types";
import { FILE_EXTENSIONS_WHITELIST } from "@/server/services/uploads/FILE_UPLOAD_WHITELIST";

export const FieldBlockConditionalLogicOperatorSchema = z
  .union([
    z.literal("exact"),
    z.literal("contains"),
    z.literal("icontains"),
    z.literal("in"),
    z.literal("startswith"),
    z.literal("gte"),
    z.literal("gt"),
    z.literal("lte"),
    z.literal("lt"),
    z.literal("range"),
    z.literal("isEmpty"),
    z.literal("isNotEmpty"),
  ])
  .describe(
    "The operator used in conditional logic for field blocks. It defines how the value of a field should be compared to the specified value in the conditional logic item."
  );

export type FieldBlockConditionalLogicOperator = z.infer<
  typeof FieldBlockConditionalLogicOperatorSchema
>;

export const FieldBlockConditionalLogicItemSchema = z.object({
  fieldId: z
    .string()
    .cuid()
    .describe(
      "CUID of the field block to apply the conditional logic to. This is the `config.id` property of the field block."
    ),
  operator: FieldBlockConditionalLogicOperatorSchema,
  value: z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.array(z.union([z.string(), z.number(), z.boolean()])),
  ]).describe(`The value to compare against the field's value.
    Depending on the field input type, some values may be restricted.
    Special cases include:
    - Checkbox fields: Valid values are 'true' or 'false'.
    - Yes/No fields: Valid values are 'true' or 'false'.
    - File Upload fields: Valid values are 'true' (a file is uploaded) or 'false' (no file is uploaded).
    - Multiple File Upload fields: Valid values are 'true' (at least one file is uploaded) or 'false' (no files are uploaded).
    - Number fields: Valid values are numbers.
    - Currency fields: Valid values are numbers.
    - Date Picker fields: Valid values are ISO date strings (e.g., "2023-10-01T00:00:00Z").
    `),
});

export type FieldBlockConditionalLogicItem = z.infer<
  typeof FieldBlockConditionalLogicItemSchema
>;

const conditionIndexSchema = z
  .number()
  .int()
  .nonnegative()
  .describe(
    "This is the index of the conditional logic rule defined in the `conditionalLogic` property of the field"
  );
type ConditionIndex = z.infer<typeof conditionIndexSchema>;

export type ConditionGrouping =
  | {
      and: (ConditionIndex | ConditionGrouping)[];
    }
  | {
      or: (ConditionIndex | ConditionGrouping)[];
    };

export const conditionGroupingSchema: ZodSchema<ConditionGrouping> = z
  .lazy(() =>
    z.union([
      z.object({
        and: z.array(z.union([conditionIndexSchema, conditionGroupingSchema])),
      }),
      z.object({
        or: z.array(z.union([conditionIndexSchema, conditionGroupingSchema])),
      }),
    ])
  )
  .describe(
    "Schema for grouping conditional logic rules using 'and' or 'or' operators. This allows for complex nested conditional logic where multiple conditions can be combined with logical operators. Each grouping can contain condition indices (references to items in the conditionalLogic array) or nested groupings for more complex logic structures."
  )
  .openapi({ ref: "ConditionalLogicGrouping" });
// Without manually registering, openapi fails to build because of circular references

export type ConditionalLogicGrouping = z.infer<typeof conditionGroupingSchema>;

export type ConditionalLogicGroupingOperator = "and" | "or";
export type ConditionalLogicGroupingOperators =
  ConditionalLogicGroupingOperator[];

// This value is something that seemed a "good" limit.
// If we hear from customers for more, we can increase it.
export const MAX_CONDITIONS_ON_FIELD = 5;

export const FieldBlockConfigBaseSchema = z.object({
  id: BlockIdSchema.optional(),
  name: z.string(),
  nameAutoGenerated: z.boolean().default(false).optional(),
  required: z.boolean(),
  hidden: z.boolean().optional(),
  label: z.string(),
  placeholder: z.string().optional(),
  helpText: z.string().optional(),
  defaultValue: z.string().optional(),
  conditionalLogic: z
    .array(FieldBlockConditionalLogicItemSchema)
    .max(MAX_CONDITIONS_ON_FIELD)
    .default([]),
  conditionalLogicGrouping: conditionGroupingSchema.optional(),
  readOnly: z.boolean().default(false).optional(),
});

// The maximum lengths correspond to the limits in tables
// https://gitlab.com/zapier/team-fpv/tablestore/-/blob/master/recordstore/constants.py?ref_type=heads#L24
export const SHORT_TEXT_FIELD_MAX_LENGTH = 255;
export const TEXTAREA_FIELD_MAX_LENGTH = 10000;

const FieldBlockUrlInputTypeSchema = z.union([
  z.literal("url"),
  z.literal("multi-url"),
]);

export const FieldBlockUrlConfigSchema = FieldBlockConfigBaseSchema.extend({
  tablesFieldKey: z
    .string()
    .optional()
    .describe(getTableFieldKeyDescription({ fieldType: "url" })),
  inputType: FieldBlockUrlInputTypeSchema,
}).openapi({ ref: "InterfaceFieldBlockUrlConfig" });

const textInputType = "text";
const FieldBlockTextInputTypeSchema = z.literal(textInputType);
export const FieldBlockTextConfigSchema = FieldBlockConfigBaseSchema.extend({
  tablesFieldKey: z
    .string()
    .optional()
    .describe(getTableFieldKeyDescription({ fieldType: textInputType })),
  inputType: FieldBlockTextInputTypeSchema,
  maxLength: z.coerce
    .number({ message: "Max length must be a number" })
    .min(1, "Minimum length must be at least 1")
    .max(
      SHORT_TEXT_FIELD_MAX_LENGTH,
      `Maximum length allowed is ${SHORT_TEXT_FIELD_MAX_LENGTH}`
    )
    .default(SHORT_TEXT_FIELD_MAX_LENGTH),
}).openapi({
  ref: "InterfaceFieldBlockTextConfig",
});

const textAreaInputType = "textarea";
const FieldBlockTextAreaInputTypeSchema = z.literal(textAreaInputType);
export const FieldBlockTextAreaConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    tablesFieldKey: z
      .string()
      .optional()
      .describe(getTableFieldKeyDescription({ fieldType: textAreaInputType })),
    inputType: FieldBlockTextAreaInputTypeSchema,
    maxLength: z.coerce
      .number({ message: "Max length must be a number" })
      .min(1, "Minimum length must be at least 1")
      .max(
        TEXTAREA_FIELD_MAX_LENGTH,
        `Maximum length allowed is ${TEXTAREA_FIELD_MAX_LENGTH}`
      )
      .default(TEXTAREA_FIELD_MAX_LENGTH),
  }
).openapi({ ref: "InterfaceFieldBlockTextAreaConfig" });

const dropdownInputType = "dropdown";
const FieldBlockDropdownInputTypeSchema = z.literal(dropdownInputType);
export const FieldBlockDropDownConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    tablesFieldKey: z
      .string()
      .optional()
      .describe(getTableFieldKeyDescription({ fieldType: dropdownInputType })),
    inputType: FieldBlockDropdownInputTypeSchema,
    options: z.string().optional(),
    multiSelect: z.boolean().optional(),
    tableId: TableIdSchema.optional().describe(
      "The ID of the Zapier Table connected to this field."
    ),
    tableFieldId: z.number().or(z.string()).optional(),
  }
).openapi({ ref: "InterfaceFieldBlockDropdownConfig" });

const numberInputType = "number";
const FieldBlockNumberInputTypeSchema = z.literal(numberInputType);
export const FieldBlockNumberConfigSchema = FieldBlockConfigBaseSchema.extend({
  tablesFieldKey: z
    .string()
    .optional()
    .describe(getTableFieldKeyDescription({ fieldType: numberInputType })),
  inputType: FieldBlockNumberInputTypeSchema,
  min: z.string().optional(),
  max: z.string().optional(),
}).openapi({ ref: "InterfaceFieldBlockNumberConfig" });

const yesNoInputType = "yes-no";
const FieldBlockYesNoInputTypeSchema = z.literal(yesNoInputType);
export const FieldBlockYesNoConfigSchema = FieldBlockConfigBaseSchema.extend({
  tablesFieldKey: z
    .string()
    .optional()
    .describe(getTableFieldKeyDescription({ fieldType: yesNoInputType })),
  inputType: FieldBlockYesNoInputTypeSchema,
}).openapi({ ref: "InterfaceFieldBlockYesNoConfig" });

const emailInputType = "email";
const FieldBlockEmailInputTypeSchema = z.literal(emailInputType);
export const FieldBlockEmailConfigSchema = FieldBlockConfigBaseSchema.extend({
  tablesFieldKey: z
    .string()
    .optional()
    .describe(getTableFieldKeyDescription({ fieldType: emailInputType })),
  inputType: FieldBlockEmailInputTypeSchema,
  blockFreeEmailProviders: z.boolean().optional(),
  restrictDomains: z.boolean().optional(),
  allowedDomains: z.string().optional(),
}).openapi({ ref: "InterfaceFieldBlockEmailConfig" });

export const DateFormatOptions = [
  {
    label: "YYYY-MM-DD",
    value: "yyyy-MM-dd",
  },
  {
    label: "MM-DD-YYYY",
    value: "MM-dd-yyyy",
  },
  {
    label: "DD-MM-YYYY",
    value: "dd-MM-yyyy",
  },
];

const datePickerInputType = "date-picker";
const FieldBlockDatepickerInputTypeSchema = z.literal(datePickerInputType);
export const FieldBlockDatepickerConfigSchema =
  FieldBlockConfigBaseSchema.extend({
    tablesFieldKey: z
      .string()
      .optional()
      .describe(
        getTableFieldKeyDescription({ fieldType: datePickerInputType })
      ),
    inputType: FieldBlockDatepickerInputTypeSchema,
    dateFormat: z.string().optional(),
    includeTime: z.boolean().default(false),
    defaultToNow: z.boolean().default(false),
  }).openapi({ ref: "InterfaceFieldBlockDatepickerConfig" });

export const PhoneNumberFormatOptions = [
  {
    label: "(555) 555-1212",
    value: "phone_number_format_1",
  },
  {
    label: "+1 (555) 555-1212",
    value: "phone_number_format_2",
  },
  {
    label: "+44 55 1234 5678",
    value: "phone_number_format_3",
  },
];

const phoneNumberInputType = "phone-number";
const FieldBlockPhoneNumberInputTypeSchema = z.literal(phoneNumberInputType);
export const FieldBlockPhoneNumberConfigSchema =
  FieldBlockConfigBaseSchema.extend({
    tablesFieldKey: z
      .string()
      .optional()
      .describe(
        getTableFieldKeyDescription({ fieldType: phoneNumberInputType })
      ),
    inputType: FieldBlockPhoneNumberInputTypeSchema,
    phoneNumberFormat: z.string().default("phone_number_format_1"),
    defaultCountry: z.string().length(2).optional().default("US"),
  }).openapi({ ref: "InterfaceFieldBlockPhoneNumberConfig" });

const checkboxInputType = "checkbox";
const FieldBlockCheckboxInputTypeSchema = z.literal(checkboxInputType);
export const FieldBlockCheckboxConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    tablesFieldKey: z
      .string()
      .optional()
      .describe(getTableFieldKeyDescription({ fieldType: checkboxInputType })),
    inputType: FieldBlockCheckboxInputTypeSchema,
    label: z.string().optional().default(""),
    text: z.string(),
    defaultChecked: z.boolean().default(false),
  }
).openapi({ ref: "InterfaceFieldBlockCheckboxConfig" });

const MEGABYTE = 1024 * 1024;

export const FREE_FILE_SIZE = 5 * MEGABYTE;
export const PREMIUM_FILE_SIZE = 10 * MEGABYTE;
export const ADVANCED_FILE_SIZE = 25 * MEGABYTE;

// The max file counts are arbitrary. Given to us by product team.
// Engineering team suggested a max file count of 100 instead of unlimited to reduce scope and engineering complexity.
export const FREE_USER_MAX_ALLOWED_FILE_COUNT = 3;
export const PAID_USERS_MAX_ALLOWED_FILE_COUNT = 100;

const fileUploadInputType = "file-upload";
const FieldBlockFileUploadInputTypeSchema = z.literal(fileUploadInputType);
export const FieldBlockFileUploadSchema = FieldBlockConfigBaseSchema.extend({
  tablesFieldKey: z
    .string()
    .optional()
    .describe(getTableFieldKeyDescription({ fieldType: fileUploadInputType })),
  inputType: FieldBlockFileUploadInputTypeSchema,
  allowedFileTypes: z
    .string()
    .optional()
    .describe(
      `File types that are allowed to be selected from the File Chooser dialog.
      - This is a comma-separated list of file extensions, e.g., .jpg, .png, .pdf.
      - The list of allowed file extensions are: ${FILE_EXTENSIONS_WHITELIST.join(",")}
      - The file extensions MUST include the leading dot, e.g., .jpg, .png, .pdf.
      - DO NOT provide mimetypes here, only file extensions are allowed.`
    ),
  allowedFileSize: z
    .number()
    .optional()
    .describe(
      `Maximum file size in bytes allowed for the file. The allowed size varies based on the user's plan.
      The file size options based on the user's plan are:
      - Free plan: ${FREE_FILE_SIZE}
      - Premium plan: ${PREMIUM_FILE_SIZE}
      - Advanced plan: ${ADVANCED_FILE_SIZE}
      `
    ),
}).openapi({ ref: "InterfaceFieldBlockFileUploadConfig" });

const multipleFileUploadInputType = "multiple-file-upload";
const FieldBlockMultipleFileUploadInputTypeSchema = z.literal(
  multipleFileUploadInputType
);
export const FieldBlockMultipleFileUploadSchema =
  FieldBlockConfigBaseSchema.extend({
    tablesFieldKey: z
      .string()
      .optional()
      .describe(
        getTableFieldKeyDescription({ fieldType: multipleFileUploadInputType })
      ),
    inputType: FieldBlockMultipleFileUploadInputTypeSchema,
    allowedFileTypes: z
      .string()
      .optional()
      .describe(
        `File types that are allowed to be selected from the File Chooser dialog.
        - This is a comma-separated list of file extensions, e.g., .jpg, .png, .pdf.
        - The list of allowed file extensions are: ${FILE_EXTENSIONS_WHITELIST.join(",")}
        - The file extensions MUST include the leading dot, e.g., .jpg, .png, .pdf.
        - DO NOT provide mimetypes here, only file extensions are allowed.`
      ),
    allowedFileSize: z
      .number()
      .optional()
      .describe(
        `Maximum file size in bytes allowed for each uploaded file. The allowed size varies based on the user's plan.
        The file size options based on the user's plan are:
        - Free plan: ${FREE_FILE_SIZE}
        - Premium plan: ${PREMIUM_FILE_SIZE}
        - Advanced plan: ${ADVANCED_FILE_SIZE}
        `
      ),
    allowedFileCount: z.number().optional()
      .describe(`Maximum number of files that can be uploaded. This is based on the user's plan.
      - Free plan: ${FREE_USER_MAX_ALLOWED_FILE_COUNT}
      - Premium plan: ${PAID_USERS_MAX_ALLOWED_FILE_COUNT}
      - Advanced plan: ${PAID_USERS_MAX_ALLOWED_FILE_COUNT}.
    `),
  });

const aiFormulaInputType = "ai-formula";
const FieldBlockAIFormulaInputTypeSchema = z.literal(aiFormulaInputType);
export const FieldBlockAIFormulaSchema = FieldBlockConfigBaseSchema.extend({
  tablesFieldKey: z
    .string()
    .optional()
    .describe(getTableFieldKeyDescription({ fieldType: aiFormulaInputType })),
  inputType: FieldBlockAIFormulaInputTypeSchema,
}).openapi({ ref: "InterfaceFieldBlockAIFormulaConfig" });

const multipleLinkedRecordInputType = "multiple-linked-record";
const FieldBlockMultipleLinkedRecordInputTypeSchema = z.literal(
  multipleLinkedRecordInputType
);
export const FieldBlockMultipleLinkedRecordsSchema =
  FieldBlockConfigBaseSchema.extend({
    tablesFieldKey: z
      .string()
      .optional()
      .describe(
        getTableFieldKeyDescription({
          fieldType: multipleLinkedRecordInputType,
        })
      ),
    inputType: FieldBlockMultipleLinkedRecordInputTypeSchema,
  }).openapi({ ref: "InterfaceFieldBlockMultipleLinkedRecordsConfig" });

const linkedRecordInputType = "linked-record";
const FieldBlockLinkedRecordInputTypeSchema = z.literal(linkedRecordInputType);
export const FieldBlockLinkedRecordSchema = FieldBlockConfigBaseSchema.extend({
  tablesFieldKey: z
    .string()
    .optional()
    .describe(
      getTableFieldKeyDescription({ fieldType: linkedRecordInputType })
    ),
  inputType: FieldBlockLinkedRecordInputTypeSchema,
}).openapi({ ref: "InterfaceFieldBlockLinkedRecordConfig" });

const formulaInputType = "formula";
const FieldBlockFormulaInputTypeSchema = z.literal(formulaInputType);
export const FieldBlockFormulaSchema = FieldBlockConfigBaseSchema.extend({
  tablesFieldKey: z
    .string()
    .optional()
    .describe(getTableFieldKeyDescription({ fieldType: formulaInputType })),
  inputType: FieldBlockFormulaInputTypeSchema,
}).openapi({ ref: "InterfaceFieldBlockFormulaConfig" });

export const CurrencyFormatOptions = [
  {
    label: "Default ($20.11)",
    value: "usd1",
  },
  {
    label: "USD ($20.11)",
    value: "usd2",
  },
  {
    label: "Euro (20.11€)",
    value: "euro1",
  },
  {
    label: "Pound (£20.11)",
    value: "pound1",
  },
  {
    label: "Yen (¥20.11)",
    value: "yen1",
  },
] as const;

export const CurrencyDecimalOptions = [
  {
    label: "00",
    value: 0,
  },
  {
    label: "0.00",
    value: 2,
  },
] as const;

const currencyInputType = "currency";
const FieldBlockCurrencyInputTypeSchema = z.literal(currencyInputType);
export const FieldBlockCurrencyConfigSchema = FieldBlockConfigBaseSchema.extend(
  {
    tablesFieldKey: z
      .string()
      .optional()
      .describe(getTableFieldKeyDescription({ fieldType: currencyInputType })),
    inputType: FieldBlockCurrencyInputTypeSchema,
    decimals: z.number().default(CurrencyDecimalOptions[0].value),
    currencyFormat: z.string().default(CurrencyFormatOptions[0].value),
  }
).openapi({ ref: "InterfaceFieldBlockCurrencyConfig" });

export const FieldBlockConfigSchema = z.union([
  FieldBlockUrlConfigSchema,
  FieldBlockTextConfigSchema,
  FieldBlockTextAreaConfigSchema,
  FieldBlockDropDownConfigSchema,
  FieldBlockNumberConfigSchema,
  FieldBlockYesNoConfigSchema,
  FieldBlockEmailConfigSchema,
  FieldBlockDatepickerConfigSchema,
  FieldBlockPhoneNumberConfigSchema,
  FieldBlockCheckboxConfigSchema,
  FieldBlockFileUploadSchema,
  FieldBlockCurrencyConfigSchema,
  FieldBlockAIFormulaSchema,
  FieldBlockMultipleLinkedRecordsSchema,
  FieldBlockLinkedRecordSchema,
  FieldBlockMultipleFileUploadSchema,
  FieldBlockFormulaSchema,
]);

/**
 * We have this as a separate schema as to avoid circular references when deriving the description for the `tableFieldKey` field.
 *
 * If we did not have this, we would have to derive the `FieldTypes` from the schemas above.
 * But then, we would try to reference the `FieldTypes` type in `getTableFieldKeyDescription` which would cause a circular reference.
 */
export const FieldBlockInputTypeSchema = z.union([
  FieldBlockUrlInputTypeSchema,
  FieldBlockTextInputTypeSchema,
  FieldBlockTextAreaInputTypeSchema,
  FieldBlockDropdownInputTypeSchema,
  FieldBlockNumberInputTypeSchema,
  FieldBlockYesNoInputTypeSchema,
  FieldBlockEmailInputTypeSchema,
  FieldBlockDatepickerInputTypeSchema,
  FieldBlockPhoneNumberInputTypeSchema,
  FieldBlockCheckboxInputTypeSchema,
  FieldBlockFileUploadInputTypeSchema,
  FieldBlockCurrencyInputTypeSchema,
  FieldBlockAIFormulaInputTypeSchema,
  FieldBlockMultipleLinkedRecordInputTypeSchema,
  FieldBlockLinkedRecordInputTypeSchema,
  FieldBlockMultipleFileUploadInputTypeSchema,
  FieldBlockFormulaInputTypeSchema,
]);

export const FieldBlockSchema = BlockBaseSchema.extend({
  type: z.literal(blockType),
  config: FieldBlockConfigSchema,
}).openapi({ ref: "FieldBlock" });

function getTableFieldKeyDescription({ fieldType }: { fieldType: FieldTypes }) {
  /**
   /**
    * The explicit type cast is not necessary, but it is included intentionally.
    * It ensures we handle the case where `foundTableFieldTypes` is empty or undefined.
    *
    * If you remove this type cast, TypeScript will correctly flag the check for `length == 0`
    * as an error, since our mapping is exhaustive and it can deduce that `foundTableFieldTypes` CANNOT be undefined, but this might change over time.
    */
  const foundTableFieldTypes = fieldBlockTypeToTableFieldTypes[fieldType] as
    | Array<string>
    | undefined;
  if (!foundTableFieldTypes || foundTableFieldTypes.length === 0) {
    throw new Error(
      `Field type ${fieldType} is not supported by any Field Block.`
    );
  }
  const formattedTableFieldTypes = foundTableFieldTypes.join(", or ");

  return `The field key that points to the field in the connected Zapier Table. Has the format f<table-field-id>, e.g., f1, f2, etc. MUST point to a ${formattedTableFieldTypes} field type in the connected Zapier Table.`;
}
